/**
 * Push notifications in the App Store / Google Play app (owner 04.11.2026). The page is loaded from
 * hub.360face.pt inside the Capacitor shell (mobile/), which injects window.Capacitor with the native
 * PushNotifications plugin (@capacitor/push-notifications). In a browser nothing here does anything.
 */
export interface PushPrefs {
  reco: boolean;
  hot: boolean;
  locations: string[];
}

type Listener = { remove: () => Promise<void> };
interface PushPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(event: 'registration', cb: (t: { value: string }) => void): Promise<Listener>;
  addListener(event: 'registrationError', cb: (e: { error: string }) => void): Promise<Listener>;
  addListener(
    event: 'pushNotificationActionPerformed',
    cb: (a: { notification: { data?: Record<string, unknown> } }) => void,
  ): Promise<Listener>;
}
interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
  registerPlugin?: <T>(name: string) => T;
  Plugins?: Record<string, unknown>;
}

const PREFS_KEY = 'hub.app.push';
const TOKEN_KEY = 'hub.app.push.token';
const ME_TOKEN = 'hub-me-token';

function cap(): CapacitorGlobal | undefined {
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function nativePlatform(): 'ios' | 'android' | undefined {
  const c = cap();
  if (!c?.isNativePlatform?.()) return undefined;
  const p = c.getPlatform?.();
  return p === 'ios' || p === 'android' ? p : undefined;
}

function plugin(): PushPlugin | undefined {
  const c = cap();
  if (!nativePlatform()) return undefined;
  if (c?.isPluginAvailable && !c.isPluginAvailable('PushNotifications')) return undefined;
  return (
    (c?.Plugins?.PushNotifications as PushPlugin | undefined) ?? c?.registerPlugin?.<PushPlugin>('PushNotifications')
  );
}

/** The app build has the push plugin (older TestFlight builds do not). */
export function pushAvailable(): boolean {
  return Boolean(plugin());
}

function read(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function write(k: string, v?: string) {
  try {
    if (v === undefined) window.localStorage.removeItem(k);
    else window.localStorage.setItem(k, v);
  } catch {
    // storage blocked: asked again next time
  }
}

export function savedPrefs(): PushPrefs | undefined {
  try {
    const p = JSON.parse(read(PREFS_KEY) ?? 'null') as PushPrefs | null;
    return p && typeof p.reco === 'boolean' ? p : undefined;
  } catch {
    return undefined;
  }
}

/** Asks iOS/Android for the permission (once) and returns the device token. */
async function deviceToken(p: PushPlugin): Promise<string> {
  let perm = await p.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await p.requestPermissions();
  if (perm.receive !== 'granted') throw new Error('denied');
  return new Promise<string>((resolve, reject) => {
    const subs: Listener[] = [];
    const done = () => subs.forEach((s) => void s.remove());
    const timer = setTimeout(() => {
      done();
      reject(new Error('timeout'));
    }, 20_000);
    void p
      .addListener('registration', (t) => {
        clearTimeout(timer);
        done();
        resolve(t.value);
      })
      .then((s) => subs.push(s));
    void p
      .addListener('registrationError', () => {
        clearTimeout(timer);
        done();
        reject(new Error('registration'));
      })
      .then((s) => subs.push(s));
    void p.register();
  });
}

export type EnableResult = 'ok' | 'denied' | 'unavailable' | 'failed';

/** Saves the choice and registers the device with Hub (signed in → linked to the client). */
export async function enablePush(
  prefs: PushPrefs,
  lang: string,
  fetchImpl: typeof fetch = (...a) => fetch(...a),
): Promise<EnableResult> {
  const p = plugin();
  const platform = nativePlatform();
  if (!p || !platform) return 'unavailable';
  let token: string;
  try {
    token = await deviceToken(p);
  } catch (e) {
    return (e as Error).message === 'denied' ? 'denied' : 'failed';
  }
  const me = read(ME_TOKEN);
  const r = await fetchImpl('/v1/me/push/device', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(me ? { Authorization: `Bearer ${me}` } : {}) },
    body: JSON.stringify({ token, platform, lang, ...prefs }),
  }).catch(() => undefined);
  if (!r?.ok) return 'failed';
  write(TOKEN_KEY, token);
  write(PREFS_KEY, JSON.stringify(prefs));
  return 'ok';
}

/** Both switches off: the device is forgotten by Hub. */
export async function disablePush(fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<void> {
  const token = read(TOKEN_KEY);
  write(PREFS_KEY, JSON.stringify({ reco: false, hot: false, locations: [] }));
  if (!token) return;
  await fetchImpl('/v1/me/push/device/remove', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  }).catch(() => undefined);
  write(TOKEN_KEY);
}

/** Sign-out: the phone keeps «hot windows», but no longer gets this client's recommendations. */
export async function detachPush(fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<void> {
  const token = read(TOKEN_KEY);
  if (!token) return;
  const prefs = savedPrefs();
  if (prefs) write(PREFS_KEY, JSON.stringify({ ...prefs, reco: false }));
  await fetchImpl('/v1/me/push/device/remove', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, detach: true }),
  }).catch(() => undefined);
}

let started = false;
/**
 * On every app start: a tap on a notification opens its page; with notifications on, the device is
 * re-registered (keeps it fresh and links it after a later sign-in). Never asks for permission here.
 */
export function startPush(lang: string, fetchImpl: typeof fetch = (...a) => fetch(...a)): void {
  const p = plugin();
  if (!p || started) return;
  started = true;
  void p.addListener('pushNotificationActionPerformed', (a) => {
    const url = a.notification.data?.url;
    if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) window.location.assign(url);
  });
  const prefs = savedPrefs();
  if (!prefs || (!prefs.reco && !prefs.hot)) return;
  void p
    .checkPermissions()
    .then((perm) => (perm.receive === 'granted' ? enablePush(prefs, lang, fetchImpl) : undefined))
    .catch(() => undefined);
}
